import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const REF_PARAM = 'ref';
const SESSION_KEY = 'dosti_ref_captured';

/**
 * F2 Referral (2.a) - capture a `?ref=<code>` from the landing URL, POST it
 * to /api/referrals/track (which sets the `dosti_ref` httpOnly cookie), and
 * strip the param from the URL so the referral code never appears in the
 * address bar or in shared links.
 *
 * Guards:
 *   - Only fires once per browsing session per code (sessionStorage flag).
 *   - Skips silently if no `?ref` present.
 *   - Never throws to the render tree; failures are logged and swallowed
 *     (referral capture must never break a landing page).
 */
export default function useReferralCapture() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const code = params.get(REF_PARAM);
      if (!code) return;

      // Idempotent per browser tab lifetime.
      if (sessionStorage.getItem(SESSION_KEY) === code) return;
      sessionStorage.setItem(SESSION_KEY, code);

      // Fire-and-forget POST. `credentials: include` so the Set-Cookie
      // response actually attaches the cookie.
      fetch(`${BACKEND}/api/referrals/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code,
          referrer_url: document.referrer || null,
        }),
      }).catch((e) => {
        console.warn('Referral capture failed (non-fatal):', e);
      });

      // Strip `?ref=` from the visible URL via React Router so BrowserRouter's
      // internal history stays in sync. Preserves other query params.
      params.delete(REF_PARAM);
      const qs = params.toString();
      navigate(
        { pathname: location.pathname, search: qs ? `?${qs}` : '', hash: location.hash },
        { replace: true },
      );
    } catch (e) {
      console.warn('useReferralCapture: unexpected error', e);
    }
    // We intentionally only want this to fire on real location changes, not
    // navigate identity. Deps: location.search change.
  }, [location.search]);
}

